export const isWeChatBrowser = () => /MicroMessenger/i.test(navigator.userAgent)

export const readOpenIdFromCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)wechat_openid=([^;]*)/)
  if (!match?.[1]) return ''
  try {
    return decodeURIComponent(match[1].trim())
  } catch {
    return match[1].trim()
  }
}

export const readLastSubmissionId = () => {
  const match = document.cookie.match(/(?:^|;\s*)ylk_last_submission=(\d+)/)
  if (match?.[1]) return match[1]
  try {
    return localStorage.getItem('ylk_last_submission') || ''
  } catch {
    return ''
  }
}

export const getStoredOpenId = () => {
  try {
    return localStorage.getItem('wechatOpenId') || sessionStorage.getItem('wechatOpenId') || readOpenIdFromCookie() || ''
  } catch {
    try {
      return sessionStorage.getItem('wechatOpenId') || readOpenIdFromCookie() || ''
    } catch {
      return readOpenIdFromCookie()
    }
  }
}

const BROWSE_MODE_KEY = 'ylk_browse_mode'

export const isBrowseMode = () => {
  try {
    return sessionStorage.getItem(BROWSE_MODE_KEY) === '1'
  } catch {
    return false
  }
}

export const enterBrowseMode = () => {
  try { sessionStorage.setItem(BROWSE_MODE_KEY, '1') } catch { /* ignore */ }
}

export const clearBrowseMode = () => {
  try { sessionStorage.removeItem(BROWSE_MODE_KEY) } catch { /* ignore */ }
}

// 同源 Cookie 用 Lax：iOS 微信内 SameSite=None 更容易丢；本站不需要跨站携带
const cookieAttrs = () => {
  const secure = location.protocol === 'https:' ? '; Secure' : ''
  return `; path=/; max-age=2592000; SameSite=Lax${secure}`
}

export const persistOpenId = (openId, submissionId) => {
  if (!openId) return
  const id = openId.trim()
  try { localStorage.setItem('wechatOpenId', id) } catch { /* ignore */ }
  try { sessionStorage.setItem('wechatOpenId', id) } catch { /* ignore */ }
  document.cookie = `wechat_openid=${encodeURIComponent(id)}${cookieAttrs()}`
  if (submissionId) {
    const sid = String(submissionId)
    try { localStorage.setItem('ylk_last_submission', sid) } catch { /* ignore */ }
    try { sessionStorage.setItem('ylk_last_submission', sid) } catch { /* ignore */ }
    document.cookie = `ylk_last_submission=${sid}${cookieAttrs()}`
  }
}

export const getWeChatOpenId = () => {
  try {
    return localStorage.getItem('wechatOpenId') || sessionStorage.getItem('wechatOpenId') || ''
  } catch {
    try {
      return sessionStorage.getItem('wechatOpenId') || ''
    } catch {
      return ''
    }
  }
}

export const saveWeChatAuthFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const openid = urlParams.get('openid')
  const token = urlParams.get('token')
  const userId = urlParams.get('userId')

  if (openid) {
    persistOpenId(openid)
    clearBrowseMode()
    if (token) {
      try { localStorage.setItem('token', token) } catch { /* ignore */ }
    }
    if (userId) {
      try { localStorage.setItem('userId', userId) } catch { /* ignore */ }
    }

    // 仅清理敏感参数，保留其它 query（如 _v），并尽量保留 openid 一段时间
    // 避免版本刷新脚本与 iOS 存储竞态导致身份丢失
    try {
      const next = new URLSearchParams(window.location.search)
      next.delete('token')
      next.delete('userId')
      // 保留 openid 在 URL 中，供后续页面跳转/刷新兜底
      const qs = next.toString()
      const cleanUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
      window.history.replaceState({}, document.title, cleanUrl)
    } catch { /* ignore */ }
    return true
  }
  return false
}

export const ensureWeChatAuth = () => {
  if (saveWeChatAuthFromUrl())
    return true

  return !!getWeChatOpenId()
}

export const requireWeChatOpenId = () => {
  if (saveWeChatAuthFromUrl())
    return getWeChatOpenId()

  const fromSession = (() => { try { return sessionStorage.getItem('wechatOpenId') || '' } catch { return '' } })()
  if (fromSession) {
    persistOpenId(fromSession)
    return fromSession
  }

  const fromCookie = readOpenIdFromCookie()
  if (fromCookie) {
    persistOpenId(fromCookie)
    return fromCookie
  }

  return getWeChatOpenId()
}

export const requireWeChatOpenIdAsync = async () => {
  if (saveWeChatAuthFromUrl()) {
    return getWeChatOpenId()
  }

  const fromUrl = new URLSearchParams(window.location.search).get('openid')
  if (fromUrl) {
    persistOpenId(fromUrl)
    return fromUrl
  }

  const fromSession = (() => { try { return sessionStorage.getItem('wechatOpenId') || '' } catch { return '' } })()
  if (fromSession) {
    persistOpenId(fromSession)
    return fromSession
  }

  const fromCookie = readOpenIdFromCookie()
  if (fromCookie) {
    persistOpenId(fromCookie)
    return fromCookie
  }

  try {
    const { wechatAPI } = await import('../api')
    const res = await wechatAPI.getCurrentOpenId()
    const oid = res.data?.OpenId || res.data?.openId
    if (oid) {
      persistOpenId(oid)
      return oid
    }
  } catch (_) { /* 404 或网络错误，忽略 */ }

  const stored = getWeChatOpenId()
  if (stored) return stored

  return ''
}
